import { memo, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withRouter } from "react-router-dom";
import { omuniWeb } from "../../../../conf";
import { setTokens } from "../../../../../auth/actions/token";
import { setUserLogin } from "../../../../../home/actions/loggedUser";
import Cookies from "js-cookie";
import { getDomain } from "../../../../../auth/helpers";
import { goToLogin } from "../../../../goToLogin";
import { setPermissions } from "../../../../../auth/actions/permissions";
import { permissionsSelector } from "../../../../../auth/selectors/permissions";
import { getLoggedUserService, getUserByIdService } from "../../../../../auth/services/loggedUser";
import { PERMISSION_ENTITIES } from "../../../../helpers/userAuth";
import { getCompanyConfigService } from "../../../../../settings/services/preferences";
import { getPreferencesSuccess } from "../../../../../settings/actions/preferences";

const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

const HOME = "/";
const PAGE_SIGNIN = "/signin";
const PAGE_TERMS = "/terms";
const PAGE_REDIRECT_LOGIN = "/redirect_login";
const PAGE_SIGNIN_RECOVER = "/signin/recover";
const getOmuniWebTarget = () => {
  const preferredHome = localStorage.getItem('omuni_preferred_home');
  if (preferredHome === 'dashboard') return '/old/dashboard';
  return `${omuniWeb}/${preferredHome || 'tickets'}`;
};

const LoggedUser = withRouter(({ history, loggedUser, validTokens, signinFetching }) => {
  const dispatch = useDispatch();
  const hasSyncedFromCookie = useRef(false);
  const hasHydratedSession = useRef(false);
  const permissionsStore = useSelector(permissionsSelector);

  // Al montar, sincronizar Redux desde el cookie.
  // Esto resuelve el caso donde omuni-web refrescó el token (actualizando el cookie)
  // pero redux-persist de omuni-crm-web todavía tiene los tokens viejos (stale).
  //
  // useLayoutEffect y no useEffect: el chequeo de sesión de más abajo también es
  // un layout effect, y esos corren ANTES que los normales. Con el sync acá, al
  // entrar por primera vez desde omuni-web --cookie válida pero store vacío,
  // porque redux-persist todavía no guardó nada-- ese chequeo veía validTokens
  // en false, te daba por deslogueado, te mandaba a /signin, y desde ahí al ver
  // la cookie buena rebotaba a omuni-web. La segunda visita funcionaba porque
  // los tokens ya estaban persistidos de la primera.
  useLayoutEffect(() => {
    if (hasSyncedFromCookie.current) return;
    hasSyncedFromCookie.current = true;

    const rawCookie = Cookies.get("session", { domain: getDomain() });
    if (!rawCookie) {
      // No hay cookie pero redux-persist puede tener tokens viejos — limpiarlos
      dispatch(setTokens(null, null, false));
      return;
    }

    try {
      const session = JSON.parse(rawCookie);
      if (session?.accessToken && session?.refreshToken) {
        // No sincronizar si el refresh token ya expiró — evita requests 401 en cascada
        if (isTokenExpired(session.refreshToken)) {
          Cookies.remove("session", { domain: getDomain() });
          // Limpiar tokens del store (pueden venir de redux-persist con valores viejos)
          dispatch(setTokens(null, null, false));
          return;
        }
        dispatch(setTokens(session.accessToken, session.refreshToken, false));
      }
    } catch {
      // cookie malformada, continuar con el flujo normal
    }
  }, [dispatch]);

  /**
   * Rehacer lo que deja la saga de login, cuando la sesión no vino de acá.
   *
   * La saga (auth/sagas/signIn) puebla tres cosas: el usuario, sus permisos y
   * las preferencias de la empresa. Desde que el acceso vive en omuni-web,
   * quien entra con una sesión ya hecha nunca la ejecuta y las tres quedan
   * vacías. Sin error visible: la app se ve recortada y rompe de a pedazos.
   *
   * Lo que se rompía: el sidebar remoto perdía todo item con permisos o
   * módulo; UserContentAvatar tiraba al leer `loggedUser.profiles[0]`;
   * CasesSocketHandler al leer `permissions.listCases`; y el header no
   * levantaba ni el softphone ni el estado del agente.
   *
   * Los permisos salen de /v2/users/me, pero el USUARIO se pide aparte con
   * /v1/users/{id}: /me devuelve otra forma --{information, authorities}-- y
   * el CRM espera los 40 campos planos de POST /access. Ver el servicio.
   *
   * `permisionEntities` y `permission` no vienen de ninguna API: son las
   * mismas constantes que pone la saga.
   */
  useEffect(() => {
    if (hasHydratedSession.current) return;

    const faltanPermisos = Object.keys(permissionsStore?.permissions || {}).length === 0;
    const faltaUsuario = !loggedUser?._id;
    if (!faltanPermisos && !faltaUsuario) return;

    let session;
    try {
      const rawCookie = Cookies.get("session", { domain: getDomain() });
      session = rawCookie ? JSON.parse(rawCookie) : null;
    } catch {
      return;
    }

    if (!session?.accessToken || isTokenExpired(session.refreshToken)) return;

    // Se marca antes de pedir: si algo falla, la app queda recortada pero no
    // entra en un bucle de requests. El próximo montaje reintenta.
    hasHydratedSession.current = true;

    const token = session.accessToken;

    // La sesión puede cerrarse mientras estas llamadas están en vuelo: el
    // logout borra la cookie y estas respuestas llegan después. Sin este
    // chequeo repoblaban el store de un usuario que ya se fue, y como tardan
    // lo que tarda la red, se veía como un rebote raro un rato más tarde.
    const sesionSigueViva = () => Boolean(Cookies.get("session", { domain: getDomain() }));

    // La cookie ya trae un subconjunto de permisos: sembrarlo evita que la
    // primera pintada del sidebar salga recortada mientras viajan las
    // llamadas. El objeto entero no entra en una cookie (~4KB), por eso
    // después se completa desde la API.
    if (session.authorities) dispatch(setPermissions(session.authorities));

    getLoggedUserService(token)
      .then(({ data }) => {
        if (!sesionSigueViva()) return null;

        const info = data?.user?.information;
        if (data?.user?.authorities) dispatch(setPermissions(data.user.authorities));
        if (!info?.id) return null;

        return getUserByIdService(token, info.id).then(({ data: userData }) => {
          if (!sesionSigueViva()) return;

          const user = userData?.user || userData;
          if (!user?._id) return;

          dispatch(
            setUserLogin({
              user: { ...user },
              permisionEntities: PERMISSION_ENTITIES,
              permission: {},
            })
          );
        });
      })
      .catch(() => {
        // Queda lo que sembró la cookie, que alcanza para el sidebar.
      });

    getCompanyConfigService(token)
      .then(({ data }) => {
        if (data && sesionSigueViva()) dispatch(getPreferencesSuccess(data));
      })
      .catch(() => {
        // Sin preferencias se pierden los items del sidebar que piden módulo.
      });
  }, [dispatch, permissionsStore, loggedUser]);

  const cleanStorage = useCallback(() => {
    localStorage.clear();
    sessionStorage.clear();
    Cookies.remove("session", { domain: getDomain() });
  }, []);

  useLayoutEffect(() => {
    const cookies = Cookies.get("session", { domain: getDomain() });

    // La cookie es la fuente de verdad de la sesión, igual que para el
    // heartbeat: la comparte con omuni-web y se renueva en cada refresh.
    const cookieSirve = (() => {
      try {
        if (!cookies) return false;
        const session = JSON.parse(cookies);
        return Boolean(
          session?.accessToken &&
            session?.refreshToken &&
            !isTokenExpired(session.refreshToken)
        );
      } catch {
        return false;
      }
    })();

    const shouldRedirect =
      history.location.pathname.startsWith(PAGE_SIGNIN) ||
      history.location.pathname === HOME;

    if (shouldRedirect && !signinFetching && cookieSirve) {
      window.location.replace(getOmuniWebTarget());
      return;
    }

    // No se mira `validTokens` del store: en el primer montaje puede estar en
    // false aunque la cookie sea buena. Pasa al entrar desde omuni-web, donde
    // redux-persist todavía no guardó nada y el sync de más arriba --aunque
    // corre antes-- no alcanza a reflejarse en las props de este mismo commit.
    // Con el store como criterio, esa primera visita se daba por deslogueada,
    // empujaba a /signin, y desde ahí rebotaba a omuni-web; la segunda andaba
    // porque los tokens ya estaban persistidos.
    const isLoggedIn = cookieSirve;

    if (!isLoggedIn) {
      // Faltaba el dispatch: las dos construian la accion y la tiraban, asi
      // que el store quedaba con el usuario y los tokens de una sesion que ya
      // no vale. Mismo descuido que tenia la saga de logout.
      dispatch(setTokens());
      dispatch(setUserLogin());

      //Valida si es una ruta privada
      const isPrivateRoute =
        history.location.pathname !== PAGE_SIGNIN &&
        history.location.pathname !== PAGE_TERMS &&
        !history.location.pathname.includes(PAGE_REDIRECT_LOGIN) &&
        !history.location.pathname.includes(PAGE_SIGNIN_RECOVER);

      if (!isPrivateRoute) return;

      goToLogin();
    }
  }, [history.location, validTokens, cleanStorage, loggedUser, dispatch]);

  return null;
});

export default memo(LoggedUser);
